import { type RefObject, useEffect, useState } from 'react';

import { SOURCE_CARD_FLASH_MS } from './constants';

/** Centers a source card inside a scrollable list. */
function scrollSourceCardIntoView(list: HTMLElement, card: HTMLElement): void {
  const delta =
    card.getBoundingClientRect().top - list.getBoundingClientRect().top;
  const top =
    list.scrollTop + delta - (list.clientHeight - card.offsetHeight) / 2;
  list.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

type UseTargetSourceScrollIntoViewOptions = {
  cardRefs: RefObject<Map<number, HTMLDivElement>>;
  isOpen: boolean;
  listRef: RefObject<HTMLDivElement | null>;
  messageId?: string;
  targetSource?: number | null;
};

/** Scrolls the sources list to a citation target and briefly flashes its card. */
export function useTargetSourceScrollIntoView({
  cardRefs,
  isOpen,
  listRef,
  messageId,
  targetSource,
}: UseTargetSourceScrollIntoViewOptions) {
  const [flashedCard, setFlashedCard] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen || !targetSource) {
      return;
    }
    const number = targetSource;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const frame = requestAnimationFrame(() => {
      if (cancelled) {
        return;
      }
      const node = cardRefs.current.get(number);
      const list = listRef.current;
      if (node && list) {
        scrollSourceCardIntoView(list, node);
      } else {
        node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      setFlashedCard(number);
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          setFlashedCard(null);
        }
      }, SOURCE_CARD_FLASH_MS);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [cardRefs, isOpen, listRef, messageId, targetSource]);

  return flashedCard;
}
